import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spin, message, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  useGetPatientQuery,
  useMergePatientsMutation,
} from '@/state/slices/patients';
import type { OverrideableField, Overrides } from './types';
import PatientCompareCard from './PatientCompareCard';
import MergedPreview from './MergedPreview';
import ConfirmationBanner from './ConfirmationBanner';
import { computeMergedRecord } from './utils/computeMergedRecord';
import styles from './MergePatientsModal.module.css';

interface MergePatientsModalProps {
  open: boolean;
  uuids: [string, string] | null;
  onClose: () => void;
  onMerged?: () => void;
}

export default function MergePatientsModal({
  open,
  uuids,
  onClose,
  onMerged,
}: MergePatientsModalProps) {
  const navigate = useNavigate();
  const [messageApi, messageContextHolder] = message.useMessage();

  const [primaryUuid, setPrimaryUuid] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Overrides>({});

  const [uuidA, uuidB] = uuids ?? [undefined, undefined];
  const patientAQuery = useGetPatientQuery(uuidA ?? '', { skip: !uuidA });
  const patientBQuery = useGetPatientQuery(uuidB ?? '', { skip: !uuidB });
  const patientA = patientAQuery.data;
  const patientB = patientBQuery.data;

  const [mergePatients, { isLoading: isMerging }] = useMergePatientsMutation();

  // Reset internal state whenever modal opens with a fresh pair.
  useEffect(() => {
    if (open) {
      setPrimaryUuid(null);
      setOverrides({});
    }
  }, [open, uuidA, uuidB]);

  const { primary, secondary } = useMemo(() => {
    if (!patientA || !patientB || !primaryUuid) {
      return { primary: null, secondary: null };
    }
    if (primaryUuid === patientA.uuid) {
      return { primary: patientA, secondary: patientB };
    }
    return { primary: patientB, secondary: patientA };
  }, [patientA, patientB, primaryUuid]);

  const merged = useMemo(() => {
    if (!primary || !secondary) return null;
    return computeMergedRecord(primary, secondary, overrides);
  }, [primary, secondary, overrides]);

  const isLoadingPatients = patientAQuery.isLoading || patientBQuery.isLoading;
  const hasLoadError = patientAQuery.isError || patientBQuery.isError;

  const handleOverrideChange = (field: OverrideableField, value: string) => {
    setOverrides((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirm = async () => {
    if (!primary || !secondary) return;
    try {
      const survivor = await mergePatients({
        primaryUuid: primary.uuid,
        secondaryUuid: secondary.uuid,
        overrides,
      }).unwrap();
      // Static API survives the navigate() that unmounts this component.
      notification.success({
        title: 'Patients merged successfully',
        description:
          'All studies have been transferred to the primary patient record.',
        placement: 'topRight',
      });
      onMerged?.();
      onClose();
      navigate(`/patients/${survivor.uuid}`);
    } catch {
      messageApi.error('Merge failed. Please try again.');
    }
  };

  const canConfirm = !!primary && !!secondary && !isMerging;

  const renderBody = () => {
    if (isLoadingPatients) {
      return (
        <div className={styles.loader}>
          <Spin />
        </div>
      );
    }
    if (hasLoadError || !patientA || !patientB) {
      return (
        <div className={styles.error}>
          Failed to load patient details. Please close and try again.
        </div>
      );
    }
    return (
      <div className={styles.body}>
        <p className={styles.instruction}>
          Select which record to keep as the Primary Patient. All studies will
          move to the primary, and the secondary will be marked merged and
          removed from the active list. This action cannot be undone.
        </p>

        <div className={styles.compareRow}>
          <PatientCompareCard
            patient={patientA}
            isPrimary={primaryUuid === patientA.uuid}
            onSetPrimary={() => setPrimaryUuid(patientA.uuid)}
            studyCount={patientA.studyCount ?? 0}
          />
          <PatientCompareCard
            patient={patientB}
            isPrimary={primaryUuid === patientB.uuid}
            onSetPrimary={() => setPrimaryUuid(patientB.uuid)}
            studyCount={patientB.studyCount ?? 0}
          />
        </div>

        {merged ? (
          <MergedPreview
            merged={merged}
            overrides={overrides}
            onOverrideChange={handleOverrideChange}
          />
        ) : (
          <div className={styles.pickPrompt}>
            Choose a primary record above to preview the merged result.
          </div>
        )}

        <ConfirmationBanner />
      </div>
    );
  };

  return (
    <>
      {messageContextHolder}
      <Modal
        title="Merge Patients"
        open={open}
        onCancel={onClose}
        width={960}
        centered
        destroyOnClose
        maskClosable={!isMerging}
        keyboard={!isMerging}
        footer={
          <div className={styles.footer}>
            <Button onClick={onClose} disabled={isMerging}>
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleConfirm}
              disabled={!canConfirm}
              loading={isMerging}
            >
              Merge Patients
            </Button>
          </div>
        }
      >
        {renderBody()}
      </Modal>
    </>
  );
}
